import React, { useState } from 'react';
import { db } from '../../config/firebase';
import { collection, query, where, getDocs, writeBatch, doc } from 'firebase/firestore';

const UpdateProductPrices = () => {
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({ updated: 0, discontinued: 0, notFound: 0 });

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setStatus('');
    setResults({ updated: 0, discontinued: 0, notFound: 0 });
  };

  const parseCSV = (text) => {
    // Basic CSV parser that handles quotes and multiple lines
    const lines = text.split(/\r?\n/);
    if (lines.length === 0) return [];
    
    const headers = lines[0].split(',').map(h => h.trim());
    const results = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      const values = line.split(',').map(v => v.trim());
      const entry = {};
      headers.forEach((header, index) => {
        entry[header] = values[index];
      });
      results.push(entry);
    }
    return results;
  };

  const handleUpdate = async () => {
    if (!file) return;
    setLoading(true);
    setStatus('Reading file...');
    
    let updatedCount = 0;
    let discontinuedCount = 0;
    let notFoundCount = 0;

    try {
      const text = await file.text();
      const data = parseCSV(text);
      setStatus(`Processing ${data.length} records...`);

      const productsRef = collection(db, 'products');
      let batch = writeBatch(db);
      let operationCount = 0;

      for (const item of data) {
        const code = item.PLSSScode;
        if (!code) continue;

        // Find product by code
        const q = query(productsRef, where('code', '==', code));
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          console.warn(`Product not found: ${code}`);
          notFoundCount++;
          continue;
        }

        for (const productDoc of snapshot.docs) {
          const productRef = doc(db, 'products', productDoc.id);
          const currentData = productDoc.data();
          const update = {};

          const bhd = item['prices.BHD'];
          const usd = item['prices.USD'];
          const aed = item['prices.AED'];
          const sar = item['prices.SAR'];

          // A product is discontinued if all price fields are blank in the CSV
          const isDiscontinued = !bhd && !usd && !aed && !sar;

          if (isDiscontinued) {
            update.isActive = false;
            update.status = 'discontinued';
            discontinuedCount++;
          } else {
            update.prices = {
              ...(currentData.prices || {}),
              BHD: bhd ? parseFloat(bhd) : (currentData.prices?.BHD || 0),
              USD: usd ? parseFloat(usd) : (currentData.prices?.USD || 0),
              AED: aed ? parseFloat(aed) : (currentData.prices?.AED || 0),
              SAR: sar ? parseFloat(sar) : (currentData.prices?.SAR || 0)
            };
            update.isActive = true;
            update.status = 'active';
            updatedCount++;
          }
          
          update.lastPriceUpdate = new Date().toISOString();
          batch.update(productRef, update);
          operationCount++;

          // Firestore batch limit is 500
          if (operationCount >= 400) {
            await batch.commit();
            batch = writeBatch(db);
            operationCount = 0;
          }
        }
      }

      if (operationCount > 0) {
        await batch.commit();
      }

      setResults({ updated: updatedCount, discontinued: discontinuedCount, notFound: notFoundCount });
      setStatus('Complete!');
    } catch (error) {
      console.error('Update failed:', error);
      setStatus(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100">
          <div className="bg-blue-600 px-8 py-6">
            <h1 className="text-2xl font-bold text-white">Bulk Product Price Update</h1>
            <p className="text-blue-100 mt-1">Upload a CSV file to update product prices or mark as discontinued.</p>
          </div>
          
          <div className="p-8">
            <div className="space-y-8">
              {/* Upload Section */}
              <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center hover:border-blue-400 transition-colors">
                <input 
                  type="file" 
                  id="csv-upload"
                  accept=".csv" 
                  onChange={handleFileChange}
                  className="hidden"
                />
                <label htmlFor="csv-upload" className="cursor-pointer">
                  <div className="flex flex-col items-center">
                    <div className="bg-blue-50 p-4 rounded-full mb-4">
                      <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                    </div>
                    <span className="text-lg font-medium text-gray-900">{file ? file.name : 'Select CSV file'}</span>
                    <span className="text-sm text-gray-500 mt-1">Click to browse your files</span>
                  </div>
                </label>
              </div>

              {/* Action Button */}
              <button
                onClick={handleUpdate}
                disabled={!file || loading}
                className={`w-full py-4 px-6 rounded-xl text-lg font-bold text-white shadow-lg transition-all transform active:scale-[0.98] ${
                  !file || loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 hover:shadow-blue-200'
                }`}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {status}
                  </span>
                ) : 'Execute Price Update'}
              </button>

              {/* Status and Results */}
              {(status || results.updated > 0 || results.notFound > 0) && (
                <div className={`rounded-xl p-6 border ${status.startsWith('Error') ? 'bg-red-50 border-red-100' : 'bg-blue-50 border-blue-100'}`}>
                  <h3 className={`text-lg font-bold mb-3 ${status.startsWith('Error') ? 'text-red-800' : 'text-blue-800'}`}>
                    {status}
                  </h3>
                  
                  {(results.updated > 0 || results.discontinued > 0 || results.notFound > 0) && (
                    <div className="grid grid-cols-3 gap-4 mt-4">
                      <div className="bg-white p-4 rounded-lg shadow-sm border border-blue-50 text-center">
                        <div className="text-2xl font-bold text-green-600">{results.updated}</div>
                        <div className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Updated</div>
                      </div>
                      <div className="bg-white p-4 rounded-lg shadow-sm border border-blue-50 text-center">
                        <div className="text-2xl font-bold text-orange-600">{results.discontinued}</div>
                        <div className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Discontinued</div>
                      </div>
                      <div className="bg-white p-4 rounded-lg shadow-sm border border-blue-50 text-center">
                        <div className="text-2xl font-bold text-red-600">{results.notFound}</div>
                        <div className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Not Found</div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Instructions */}
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  CSV Format Requirements
                </h4>
                <div className="space-y-3 text-sm text-gray-600">
                  <p>The CSV must include the following headers exactly:</p>
                  <code className="block p-3 bg-white border border-gray-200 rounded-lg font-mono text-blue-700">
                    PLSSScode,prices.BHD,prices.USD,prices.AED,prices.SAR
                  </code>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li><span className="font-semibold text-gray-800">PLSSScode:</span> Matches the product's unique code.</li>
                    <li><span className="font-semibold text-gray-800">Price Fields:</span> Numeric values only. Leave <span className="italic underline">all</span> price fields empty to mark a product as discontinued.</li>
                    <li><span className="font-semibold text-gray-800">Merge Logic:</span> If some prices are provided and others are blank, existing prices for the blank fields will be preserved.</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateProductPrices;
